import { useEffect, useRef, useState } from "react";
import AvatarEditor from "react-avatar-editor";
import PropTypes from "prop-types";
import "./ProfileUploaderContent.scss";
import Spinner from "./app/common-components/Spinner";
import { user } from "./assets/images";

const CameraIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 8.5h2.6l1.3-2h8.2l1.3 2H20a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 20 19.5H4A1.5 1.5 0 0 1 2.5 18v-8A1.5 1.5 0 0 1 4 8.5Z" />
    <circle cx="12" cy="13.6" r="3.4" />
  </svg>
);

CameraIcon.propTypes = { size: PropTypes.number };

/* The rest of the marks in this dialog, drawn to the same weight as the camera
   above rather than pulled from an icon set — the three guidance tiles and the
   two footer buttons are the only places they appear. */
const Glyph = ({ size = 16, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

Glyph.propTypes = { size: PropTypes.number, children: PropTypes.node };

const CropIcon = (props) => (
  <Glyph {...props}>
    <path d="M6.5 2.5v15h15" />
    <path d="M2.5 6.5h15v15" />
  </Glyph>
);

const FileIcon = (props) => (
  <Glyph {...props}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5" />
  </Glyph>
);

const ShieldCheckIcon = (props) => (
  <Glyph {...props}>
    <path d="M12 3 5 5.8v5.4c0 4.2 3 7.6 7 9 4-1.4 7-4.8 7-9V5.8L12 3Z" />
    <path d="m9 12 2.2 2.2L15.5 10" />
  </Glyph>
);

const TrashIcon = (props) => (
  <Glyph {...props}>
    <path d="M3.5 6h17" />
    <path d="M8.5 6V4.5a1.5 1.5 0 0 1 1.5-1.5h4a1.5 1.5 0 0 1 1.5 1.5V6" />
    <path d="M18 6v13a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6" />
    <path d="M10.5 10.5v6M13.5 10.5v6" />
  </Glyph>
);

const SaveIcon = (props) => (
  <Glyph {...props}>
    <path d="M5 3h11l3 3v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
    <path d="M7.5 3v6h8V3" />
    <rect x="7.5" y="13" width="9" height="8" rx="1" />
  </Glyph>
);

/* Standing guidance for a good photo. Three fixed points rather than a
   paragraph — each is a rule someone can check their own image against. */
const PHOTO_TIPS = [
  {
    key: "square",
    icon: <CropIcon />,
    title: "Square image",
    hint: "1:1 ratio recommended",
  },
  {
    key: "format",
    icon: <FileIcon />,
    title: "PNG or JPG",
    hint: "Up to 150KB",
  },
  {
    key: "clarity",
    icon: <ShieldCheckIcon />,
    title: "Clear & professional",
    hint: "Better visibility",
    // The one that is advice rather than a constraint, so it reads green.
    tone: "positive",
  },
];

export default function ProfileUploaderContent({
  preview,
  alert,
  onFileChange,
  onSave,
  onRemove,
  isLoading,
  hasFileChanged,
  usersProfilePic,
  selectedFile,
  isImageRemoved,
  initial,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [livePreview, setLivePreview] = useState(null);

  const inputRef = useRef();
  const editorRef = useRef();

  /* The parent hands back the default silhouette when there is nothing on file,
     so that stand-in is what tells us the account has no photo. */
  const hasPhoto = Boolean(preview) && preview !== user;
  const showMonogram = !selectedFile && !hasPhoto && Boolean(initial);

  const openPicker = () => inputRef.current?.click();

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      onFileChange(e.dataTransfer.files[0]);
      if (inputRef.current) {
        inputRef.current.value = null;
      }
    }
  };

  const updateLivePreview = () => {
    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas();
      setLivePreview(canvas.toDataURL("image/png"));
    }
  };

  const handleSave = () => {
    if (isImageRemoved) {
      onSave(null);
      return;
    }
    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas();
      const croppedDataUrl = canvas.toDataURL("image/png");

      onSave(croppedDataUrl); // ⬅ base64 will be converted to File in parent
    }
  };

  useEffect(() => {
    if (selectedFile) {
      setTimeout(updateLivePreview, 100); // small delay to allow image load
    }
  }, [selectedFile, zoom]);

  const canRemove = !isImageRemoved && (usersProfilePic || selectedFile);
  const canSave =
    (hasFileChanged || isImageRemoved) &&
    !(!usersProfilePic && isImageRemoved && !selectedFile);

  /* The standing guidance lives in the header. `alert` only speaks up when it
     has something to report, so the two never say the same thing at once. */
  const note = alert?.show ? alert.message : "";

  return (
    // Spans, not headings or paragraphs: custom.scss forces h4 sizing and
    // modal.scss restyles every paragraph inside a modal body.
    <div className="profile-uploader">
      <div className="pu-head">
        <span className="pu-head__icon">
          <CameraIcon size={19} />
        </span>
        <span className="pu-head__text">
          <span className="pu-head__title">Profile Photo</span>
          <span className="pu-head__hint">
            A square image works best. PNG or JPG, up to 150KB.
          </span>
        </span>
      </div>

      {isLoading ? (
        <div className="pu-loading">
          <Spinner />
          <span className="pu-loading__text">Saving your photo…</span>
        </div>
      ) : (
        <>
          <div className="pu-body">
            {/* The dashed ring sits outside the circle rather than on it, so a
                photo is never crossed by a line — it reads as the target the
                image is dropped into. */}
            <div
              className={`pu-ring ${isDragging ? "pu-ring--dragging" : ""} ${
                selectedFile ? "pu-ring--editing" : ""
              }`}
            >
              <div
                className={`pu-drop ${isDragging ? "pu-drop--dragging" : ""} ${
                  selectedFile ? "pu-drop--editing" : ""
                } ${showMonogram ? "pu-drop--empty" : ""}`}
                role="button"
                tabIndex={0}
                aria-label="Change profile photo"
                onClick={selectedFile ? undefined : openPicker}
                onKeyDown={(e) => {
                  if (!selectedFile && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    openPicker();
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                {selectedFile ? (
                  <AvatarEditor
                    ref={editorRef}
                    image={selectedFile}
                    width={150}
                    height={150}
                    border={20}
                    borderRadius={75}
                    color={[255, 255, 255, 0.65]}
                    scale={zoom}
                    rotate={0}
                    onMouseUp={updateLivePreview}
                    onTouchEnd={updateLivePreview}
                  />
                ) : showMonogram ? (
                  /* No photo on file — the account's initial stands in for one,
                     the same way it does on the sidebar. */
                  <span className="pu-monogram" aria-hidden="true">
                    {initial}
                  </span>
                ) : (
                  <img src={preview || user} alt="Profile Preview" />
                )}

                {/* Only on the resting circle: while the editor is mounted the
                    same area is being dragged to reposition the crop. */}
                {!selectedFile && (
                  <span className="pu-drop__overlay">
                    <CameraIcon />
                    <span>Change</span>
                  </span>
                )}
              </div>

              {/* On the ring rather than inside the circle, so it can sit over
                  the rim the way it does on an avatar. */}
              {!selectedFile && (
                <span className="pu-ring__badge" aria-hidden="true">
                  <CameraIcon size={15} />
                </span>
              )}
            </div>

            <span className="pu-caption">
              {selectedFile ? (
                "Drag the image to reposition it."
              ) : (
                <>
                  <span className="pu-caption__lead">
                    Drag and drop an image here
                  </span>
                  <span className="pu-caption__alt">
                    or{" "}
                    {/* Drawn like a link and wired like one: the words are the
                        control, not a label sitting next to it. */}
                    <button
                      type="button"
                      className="pu-caption__action"
                      onClick={openPicker}
                    >
                      browse files
                    </button>
                  </span>
                </>
              )}
            </span>
          </div>

          {/* Only while the circle is at rest: mid-crop the dialog is about the
              image already picked, not about choosing a better one. */}
          {!selectedFile && (
            <div className="pu-tips">
              {PHOTO_TIPS.map((tip) => (
                <div className="pu-tip" key={tip.key}>
                  <span
                    className={`pu-tip__icon ${
                      tip.tone === "positive" ? "pu-tip__icon--positive" : ""
                    }`}
                  >
                    {tip.icon}
                  </span>
                  <span className="pu-tip__text">
                    <span className="pu-tip__title">{tip.title}</span>
                    <span className="pu-tip__hint">{tip.hint}</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {selectedFile && (
            <div className="pu-adjust">
              <label className="pu-zoom" htmlFor="pu-zoom-range">
                <span className="pu-zoom__label">Zoom</span>
                <input
                  id="pu-zoom-range"
                  className="pu-zoom__range"
                  type="range"
                  min={1}
                  max={2.5}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => {
                    const zoomVal = parseFloat(e.target.value);
                    setZoom(zoomVal);
                    updateLivePreview();
                  }}
                />
              </label>

              {/* What the crop will actually look like at the size it is used. */}
              <span className="pu-result">
                <span className="pu-result__ring">
                  <img src={livePreview || preview} alt="Live Preview" />
                </span>
                <span className="pu-result__label">Preview</span>
              </span>
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            ref={inputRef}
            onChange={(e) => {
              const selected = e.target.files[0];
              if (selected) {
                onFileChange(selected);
              }
              e.target.value = null;
            }}
            hidden
          />

          {note && (
            <span
              className={`pu-note ${
                alert.type === "danger" ? "pu-note--error" : ""
              }`}
              role={alert.type === "danger" ? "alert" : undefined}
            >
              {note}
            </span>
          )}

          <div className="pu-foot">
            <button
              type="button"
              className="pu-btn pu-btn--quiet pu-btn--danger"
              onClick={() => {
                onRemove();
                if (inputRef.current) {
                  inputRef.current.value = null;
                }
              }}
              disabled={!canRemove}
            >
              <TrashIcon size={15} />
              Remove
            </button>

            {/* No Upload button: the circle and the caption's browse link both
                open the picker, so a third way in was only noise. */}
            <button
              type="button"
              className="pu-btn pu-btn--primary"
              onClick={handleSave}
              disabled={!canSave}
            >
              <SaveIcon size={15} />
              Save
            </button>
          </div>
        </>
      )}
    </div>
  );
}

ProfileUploaderContent.propTypes = {
  preview: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)])
    .isRequired,
  alert: PropTypes.shape({
    show: PropTypes.bool,
    type: PropTypes.oneOf(["success", "danger", "warning", "info", ""]),
    message: PropTypes.string,
  }).isRequired,
  onFileChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  hasFileChanged: PropTypes.bool,
  usersProfilePic: PropTypes.string,
  selectedFile: PropTypes.instanceOf(File),
  isImageRemoved: PropTypes.bool,
  initial: PropTypes.string,
};
